import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import Select from '@/components/ui/select.jsx';
import { Q_EMPLEADOS } from '../graphql/queries.js';
import { M_DELETE_EMPLEADO, M_RESTORE_EMPLEADO } from '../graphql/mutations.js';
import { useEmpleadosFilters } from '../hooks/useEmpleadosFilters.js';
import { useToasts } from '../hooks/useToasts.js';
import { subscribeToApolloErrors } from '@/apollo/client.js';
import EmpleadosTable from '../components/EmpleadosTable.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import ToastStack from '../components/ToastStack.jsx';

const ESTADO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'ACTIVO', label: 'Activos' },
  { value: 'INACTIVO', label: 'Inactivos' },
  { value: 'BAJA', label: 'Baja' },
];

const inputIds = {
  search: 'empleados-search',
  estado: 'empleados-estado',
  empresa: 'empleados-empresa',
  sucursal: 'empleados-sucursal',
  fechaDesde: 'empleados-fecha-desde',
  fechaHasta: 'empleados-fecha-hasta',
};

function buildFilterPayload(filters) {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

// Página principal de listados con filtros, métricas y acciones de RRHH
export function EmpleadosListPage() {
  const navigate = useNavigate();
  const { page, pageSize, filters, sort, setFilters, setPage, setPageSize, setSort, resetFilters } =
    useEmpleadosFilters();
  const [search, setSearch] = useState(filters.search || '');
  const [pendingAction, setPendingAction] = useState(null);
  const { toasts, addToast, removeToast } = useToasts();

  useEffect(() => {
    setSearch(filters.search || '');
  }, [filters.search]);

  useEffect(() => {
    const unsubscribe = subscribeToApolloErrors(payload => addToast(payload));
    return unsubscribe;
  }, [addToast]);

  const variables = useMemo(
    () => ({
      page,
      pageSize,
      filter: buildFilterPayload(filters),
      sort,
    }),
    [filters, page, pageSize, sort],
  );

  const { data, loading, refetch } = useQuery(Q_EMPLEADOS, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  const [deleteEmpleado] = useMutation(M_DELETE_EMPLEADO, {
    onCompleted: () => {
      addToast({ type: 'success', message: 'Empleado dado de baja correctamente.' });
      refetch();
    },
    onError: () => addToast({ type: 'error', message: 'No pudimos dar de baja al empleado.' }),
    // Ejemplo: podríamos usar cache.modify para evitar refetch y actualizar la lista manualmente.
  });

  const [restoreEmpleado] = useMutation(M_RESTORE_EMPLEADO, {
    onCompleted: () => {
      addToast({ type: 'success', message: 'Empleado restaurado correctamente.' });
      refetch();
    },
    onError: () => addToast({ type: 'error', message: 'No pudimos restaurar al empleado.' }),
  });

  const total = data?.empleados?.totalCount || 0;
  const edges = data?.empleados?.edges || [];
  const activos = edges.filter(edge => edge.node.estado === 'ACTIVO').length;

  const handleSearchSubmit = event => {
    event.preventDefault();
    setFilters({ ...filters, search });
  };

  const handleEstadoChange = event => {
    setFilters({ ...filters, estado: event.target.value });
  };

  const handleEmpresaChange = event => {
    setFilters({ ...filters, empresaId: event.target.value });
  };

  const handleSucursalChange = event => {
    setFilters({ ...filters, sucursalId: event.target.value });
  };

  const handleFechaChange = event => {
    const { name, value } = event.target;
    setFilters({ ...filters, [name]: value });
  };

  const openDeleteDialog = id => {
    setPendingAction({ type: 'delete', id });
  };

  const openRestoreDialog = id => {
    setPendingAction({ type: 'restore', id });
  };

  const closeDialog = () => setPendingAction(null);

  const confirmAction = async () => {
    if (!pendingAction) return;
    const { id, type } = pendingAction;
    if (type === 'delete') {
      await deleteEmpleado({
        variables: { id },
        optimisticResponse: { deleteEmpleado: true },
      });
    } else {
      await restoreEmpleado({
        variables: { id },
        optimisticResponse: { restoreEmpleado: true },
      });
    }
    closeDialog();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-8">
      <ToastStack toasts={toasts} onDismiss={removeToast} />
      <header className="flex flex-col gap-6 rounded-3xl bg-gradient-to-r from-brand-navy/90 to-brand-teal/70 p-8 text-white shadow-[var(--shadow-soft)] md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Empleados</h1>
          <p className="mt-2 text-sm text-white/80">
            Gestiona el padrón completo: filtros, altas, bajas lógicas y restauración en segundos.
          </p>
        </div>
        <div className="flex gap-6">
          <div>
            <span className="block text-sm text-white/70">Total</span>
            <strong className="text-3xl font-bold">{total}</strong>
          </div>
          <div>
            <span className="block text-sm text-white/70">Activos visibles</span>
            <strong className="text-3xl font-bold">{activos}</strong>
          </div>
          <Button
            className="self-end rounded-2xl bg-lime-400 px-6 text-brand-navy hover:bg-lime-300"
            onClick={() => navigate('/rrhh/empleados/nuevo')}
          >
            Nuevo empleado
          </Button>
        </div>
      </header>

      <section className="rounded-3xl border border-[var(--border)] bg-[var(--panel)] p-6 shadow-[var(--shadow-soft)]">
        <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-5">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold uppercase text-[var(--text-muted)]" htmlFor={inputIds.search}>
              Buscar
            </label>
            <Input
              id={inputIds.search}
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Nombre, apellido, legajo o DNI"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-[var(--text-muted)]" htmlFor={inputIds.estado}>
              Estado
            </label>
            <Select id={inputIds.estado} value={filters.estado || ''} onChange={handleEstadoChange}>
              {ESTADO_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-[var(--text-muted)]" htmlFor={inputIds.empresa}>
              Empresa
            </label>
            <Input
              id={inputIds.empresa}
              value={filters.empresaId || ''}
              onChange={handleEmpresaChange}
              placeholder="Ej. emp-001"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-[var(--text-muted)]" htmlFor={inputIds.sucursal}>
              Sucursal
            </label>
            <Input
              id={inputIds.sucursal}
              value={filters.sucursalId || ''}
              onChange={handleSucursalChange}
              placeholder="Ej. caba"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-[var(--text-muted)]" htmlFor={inputIds.fechaDesde}>
              Ingreso desde
            </label>
            <Input
              id={inputIds.fechaDesde}
              type="date"
              name="fechaIngresoFrom"
              value={filters.fechaIngresoFrom || ''}
              onChange={handleFechaChange}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-[var(--text-muted)]" htmlFor={inputIds.fechaHasta}>
              Ingreso hasta
            </label>
            <Input
              id={inputIds.fechaHasta}
              type="date"
              name="fechaIngresoTo"
              value={filters.fechaIngresoTo || ''}
              onChange={handleFechaChange}
            />
          </div>
          <div className="flex items-end gap-3 md:col-span-2">
            <Button type="submit" className="rounded-2xl bg-brand-teal px-6 text-white hover:bg-emerald-500">
              Aplicar filtros
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="rounded-2xl"
              onClick={() => {
                setSearch('');
                resetFilters();
              }}
            >
              Limpiar
            </Button>
          </div>
        </form>
      </section>

      <EmpleadosTable
        rows={edges.map(edge => edge.node)}
        loading={loading}
        sort={sort}
        onSortChange={setSort}
        onEdit={id => navigate(`/rrhh/empleados/${id}/editar`)}
        onDelete={openDeleteDialog}
        onRestore={openRestoreDialog}
      />

      <footer className="flex flex-col items-center justify-between gap-4 rounded-3xl border border-[var(--border)] bg-[var(--panel)] p-4 shadow-[var(--shadow-soft)] md:flex-row">
        <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
          Página {page} de {totalPages}
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(pageSize)} onChange={event => setPageSize(Number(event.target.value))}>
            {[10, 20, 50].map(size => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </Select>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="rounded-2xl"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="ghost"
              className="rounded-2xl"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </footer>

      <ConfirmDialog
        open={Boolean(pendingAction)}
        title={pendingAction?.type === 'delete' ? 'Confirmar baja lógica' : 'Restaurar empleado'}
        description={
          pendingAction?.type === 'delete'
            ? 'La baja es lógica y podrás restaurar al empleado cuando lo necesites.'
            : 'El empleado volverá a figurar como activo en todos los listados.'
        }
        confirmLabel={pendingAction?.type === 'delete' ? 'Dar de baja' : 'Restaurar'}
        tone={pendingAction?.type === 'delete' ? 'danger' : 'success'}
        onCancel={closeDialog}
        onConfirm={confirmAction}
      />
    </div>
  );
}

export default EmpleadosListPage;
